import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AsyncThunk } from '@reduxjs/toolkit';
import { AppDispatch } from 'core/types';
import { VisitRequestFormResponsePayload } from 'core/visitRequestForm/types';
import { loadGuestStep, updateGuestStep, submitGuestBooking } from 'core/guestBooking/guestBookingApi';

interface LoadStepArgs {
    id: string | number;
    step: number;
    amending?: boolean;
}

interface UpdateStepArgs {
    id: string | number;
    step: number;
    data: Record<string, unknown>;
}

// The guest and authenticated thunks agree on this shape, which is what lets one
// set of screens serve both; typing the config against it keeps a chain from
// being wired up with a thunk that returns something else.
type ChainThunk<Args> = AsyncThunk<VisitRequestFormResponsePayload, Args, Record<string, never>>;

interface ChainConfig {
    /** Route prefix for the authenticated flow, e.g. 'guarding-request'. */
    basePath: string;
    /** The chain's authenticated load thunk. */
    loadThunk: ChainThunk<LoadStepArgs>;
    /** The chain's authenticated update thunk. */
    updateThunk: ChainThunk<UpdateStepArgs>;
    /** Route step number of the chain's review screen. */
    reviewStep: number;
}

/**
 * Serves a request chain's step screens to both the authenticated flow and a
 * token (guest) booking, so one set of components covers both.
 *
 * A one-off booking link can raise a visit, access or guarding request, and the
 * guest has no account and no request id — only a token. The guest endpoints
 * return the same {visitRequest, stepData} shape as the authenticated ones and
 * the chain's slice now stores either, so the only thing a component has to
 * change is going through this hook rather than dispatching directly.
 *
 * The visit request chain has its own older equivalent (useRequestFlow); this
 * is the same idea parameterised by chain.
 */
const useChainFlow = (config: ChainConfig) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id, token } = useParams<{ id?: string, token?: string }>();
    const guestMode = Boolean(token);

    const loadStep = (step: number, amending = false) => (
        guestMode
            ? dispatch(loadGuestStep({ token: token as string, step }))
            : dispatch(config.loadThunk({ id: id as string, step, amending }))
    );

    // The guest endpoint takes no step: the API advances the draft itself, and
    // which step that is depends on the request type.
    const updateStep = (step: number, data: Record<string, unknown>) => (
        guestMode
            ? dispatch(updateGuestStep({ token: token as string, data }))
            : dispatch(config.updateThunk({ id: id as string, step, data }))
    );

    const submitReview = (declaration: Record<string, unknown>) => (
        guestMode
            ? dispatch(submitGuestBooking({ token: token as string, declaration }))
            : dispatch(config.updateThunk({
                id: id as string,
                step: config.reviewStep,
                data: { declaration, forcedStep: config.reviewStep },
            }))
    );

    const goToStep = (step: number, state?: object) => (
        guestMode
            ? navigate(`/booking/${token}/step/${step}`, { state })
            : navigate(`/${config.basePath}/${id}/step/${step}`, { state })
    );

    return { guestMode, id, token, loadStep, updateStep, submitReview, goToStep };
};

export default useChainFlow;
